'use client'

import clsx from 'clsx'
import { ChangeEvent, useEffect, useState } from 'react'
import usePlacesAutocomplete from 'use-places-autocomplete'
import { useSnackbar } from 'notistack'
import { useTranslations } from '@/lib/next-shim'

import { Combobox } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline'

type AddressMode = 'search' | 'manual'

type PlacesAutocompleteProps = {
  defaultValue?: string
  label?: string
  onSelect?: (address: google.maps.places.AutocompletePrediction) => void
  /** Render only the autocomplete input, without the built-in manual-entry tabs/form. */
  hideManualEntry?: boolean
}

type ManualAddress = {
  street: string
  city: string
  postalCode: string
  country: string
}

export default function PlacesAutocomplete(props: PlacesAutocompleteProps) {
  const { defaultValue, label, onSelect, hideManualEntry } = props
  const t = useTranslations('address')
  const { enqueueSnackbar } = useSnackbar()
  const {
    ready,
    value,
    setValue,
    suggestions: { status, data },
  } = usePlacesAutocomplete({ debounce: 300, defaultValue })

  const [selectedSuggestion, setSelectedSuggestion] = useState<google.maps.places.AutocompletePrediction | null>(null)
  const [mode, setMode] = useState<AddressMode>('search')
  const [timedOut, setTimedOut] = useState(false)
  const [manualAddress, setManualAddress] = useState<ManualAddress>({ street: '', city: '', postalCode: '', country: '' })

  // 5-second timeout: if Google Places never becomes ready, fall back to manual entry.
  useEffect(() => {
    if (ready || hideManualEntry) return
    const timer = setTimeout(() => {
      if (!ready) setTimedOut(true)
    }, 5000)
    return () => clearTimeout(timer)
  }, [ready, hideManualEntry])

  // When Maps is unavailable, manual entry is the only option (the search tab is disabled).
  const activeMode: AddressMode = timedOut ? 'manual' : mode

  // Switching tabs discards the in-progress suggestion so a stale one doesn't linger.
  const changeMode = (next: AddressMode) => {
    if (next === activeMode) return
    setMode(next)
    setSelectedSuggestion(null)
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value)
  }

  const handleSelect = (suggestion: google.maps.places.AutocompletePrediction | null) => {
    setSelectedSuggestion(suggestion)
    if (suggestion) onSelect?.(suggestion)
  }

  const handleManualSubmit = () => {
    const { street, city, postalCode, country } = manualAddress
    if (!street || !city) return

    const description = [street, [postalCode, city].filter(Boolean).join(' '), country].filter(Boolean).join(', ')
    const fakeResult = {
      description,
      place_id: '',
      structured_formatting: {
        main_text: street,
        main_text_matched_substrings: [],
        secondary_text: `${postalCode} ${city}`,
      },
      matched_substrings: [],
      terms: [],
      types: [],
    } as google.maps.places.AutocompletePrediction

    onSelect?.(fakeResult)
    enqueueSnackbar(t('addressConfirmed'), { variant: 'success' })
  }

  const tabClassName = (isActive: boolean, disabled = false) =>
    clsx(
      'rounded px-3 py-1.5 text-sm font-medium transition-colors',
      isActive ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900',
      disabled && 'cursor-not-allowed opacity-50'
    )

  const searchInput = (
    <Combobox as="div" disabled={!ready} nullable onChange={handleSelect} value={selectedSuggestion}>
      <div className="relative">
        <Combobox.Input
          className="w-full form-input rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-primary sm:text-sm sm:leading-6"
          displayValue={(suggestion: google.maps.places.AutocompletePrediction | null) => suggestion?.description || value}
          onChange={handleChange}
        />
        <Combobox.Button className="absolute bg-transparent inset-y-0 right-0 flex items-center rounded-r-md px-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
          <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </Combobox.Button>

        {status === 'OK' && data.length > 0 && (
          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary sm:text-sm">
            {data.map((suggestion) => (
              <Combobox.Option
                key={suggestion.place_id}
                value={suggestion}
                className={({ active }) => clsx('relative cursor-default select-none py-2 pl-3 pr-9', active ? 'bg-indigo-600 text-white' : 'text-gray-900')}
              >
                {({ active, selected }) => (
                  <>
                    <span className={clsx('block truncate', selected && 'font-semibold')}>{suggestion.description}</span>

                    {selected && (
                      <span className={clsx('absolute inset-y-0 right-0 flex items-center pr-4', active ? 'text-white' : 'text-indigo-600')}>
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    )}
                  </>
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  )

  // Embedded mode (e.g. AtHomeAddressPicker): the parent owns the search/manual tabs and its own
  // manual form, so render just the autocomplete input.
  if (hideManualEntry) {
    return (
      <div className="space-y-3">
        {label && <p className="block text-sm font-medium leading-6 text-gray-900">{label}</p>}
        {searchInput}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {label && <p className="block text-sm font-medium leading-6 text-gray-900">{label}</p>}

      {/* Same-line segmented control: address search vs. manual entry */}
      <div role="tablist" className="grid grid-cols-2 gap-1 rounded-md bg-gray-100 p-1">
        <button
          type="button"
          role="tab"
          aria-selected={activeMode === 'search'}
          disabled={timedOut}
          onClick={() => changeMode('search')}
          className={tabClassName(activeMode === 'search', timedOut)}
        >
          {t('useAutocomplete')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeMode === 'manual'}
          onClick={() => changeMode('manual')}
          className={tabClassName(activeMode === 'manual')}
        >
          {t('enterManually')}
        </button>
      </div>

      {activeMode === 'manual' ? (
        <div className="space-y-3">
          {timedOut && <p className="text-sm text-amber-600">{t('mapsUnavailable')}</p>}
          <div>
            <label className="block text-sm font-medium leading-6 text-gray-900">{t('street')}</label>
            <input
              type="text"
              value={manualAddress.street}
              onChange={(e) => setManualAddress({ ...manualAddress, street: e.target.value })}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">{t('city')}</label>
              <input
                type="text"
                value={manualAddress.city}
                onChange={(e) => setManualAddress({ ...manualAddress, city: e.target.value })}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
              />
            </div>
            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">{t('postalCode')}</label>
              <input
                type="text"
                value={manualAddress.postalCode}
                onChange={(e) => setManualAddress({ ...manualAddress, postalCode: e.target.value })}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium leading-6 text-gray-900">{t('country')}</label>
            <input
              type="text"
              value={manualAddress.country}
              onChange={(e) => setManualAddress({ ...manualAddress, country: e.target.value })}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
            />
          </div>
          <button
            type="button"
            onClick={handleManualSubmit}
            disabled={!manualAddress.street || !manualAddress.city}
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
          >
            {t('address.confirmAddress')}
          </button>
        </div>
      ) : (
        searchInput
      )}
    </div>
  )
}
